// Delivery Zone Management — fees, serviceable toggle, region mapping
import { useState, useCallback, useMemo, useEffect } from "react";
import {
  Map as MapIcon,
  Pencil,
  AlertCircle,
  Truck,
  Bike,
  Clock,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Button, Skeleton } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { ModuleGuard } from "@/components/admin/modules/ModuleGuard";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { SearchInput } from "@/components/admin/shared/SearchInput";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  useDeliveryZones,
  useUpdateDeliveryZone,
} from "@/hooks/admin/useDeliveryZones";
import type {
  AdminDeliveryZone,
  UpdateDeliveryZonePayload,
} from "@/lib/admin-api/delivery";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SENEGAL_REGIONS = [
  "Dakar",
  "Kaolack",
  "Saint-Louis",
  "Fatick",
  "Thiès-Mbour",
  "Diourbel-Touba",
] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFcfa(amount: number | null): string {
  if (amount == null) return "—";
  return `${new Intl.NumberFormat("fr-SN").format(amount)} FCFA`;
}

function formatMinutes(minutes: number | null): string {
  if (minutes == null) return "—";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h${m > 0 ? `${m}min` : ""}` : `${m}min`;
}

// ---------------------------------------------------------------------------
// KPI summary cards
// ---------------------------------------------------------------------------

interface ZoneSummary {
  readonly total: number;
  readonly serviceable: number;
  readonly avgFeeCar: number;
  readonly avgTime: number;
}

function computeSummary(zones: readonly AdminDeliveryZone[]): ZoneSummary {
  const serviceable = zones.filter((z) => z.is_serviceable);
  const feeSum = serviceable.reduce(
    (acc, z) => acc + (z.fee_own_car_fcfa ?? 0),
    0,
  );
  const timeSum = serviceable.reduce(
    (acc, z) => acc + (z.estimated_time_minutes ?? 0),
    0,
  );
  const count = serviceable.length || 1;

  return {
    total: zones.length,
    serviceable: serviceable.length,
    avgFeeCar: Math.round(feeSum / count),
    avgTime: Math.round(timeSum / count),
  };
}

function SummaryCards({
  summary,
  isLoading,
}: {
  readonly summary: ZoneSummary;
  readonly isLoading: boolean;
}) {
  const cards = [
    {
      label: "Zones totales",
      value: String(summary.total),
      icon: <MapIcon className="h-4 w-4" />,
    },
    {
      label: "Zones actives",
      value: String(summary.serviceable),
      icon: <CheckCircle2 className="h-4 w-4" />,
    },
    {
      label: "Frais moyen (voiture)",
      value: formatFcfa(summary.avgFeeCar),
      icon: <Truck className="h-4 w-4" />,
    },
    {
      label: "Temps moyen estimé",
      value: formatMinutes(summary.avgTime),
      icon: <Clock className="h-4 w-4" />,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {cards.map((card) => (
        <div
          key={card.label}
          className="rounded-lg border border-stone-200 bg-white px-4 py-3"
        >
          {isLoading ? (
            <>
              <Skeleton className="mb-2 h-4 w-24" />
              <Skeleton className="h-6 w-16" />
            </>
          ) : (
            <>
              <div className="flex items-center gap-1.5 text-xs text-stone-500">
                {card.icon}
                {card.label}
              </div>
              <p className="mt-1 text-lg font-semibold text-stone-900">
                {card.value}
              </p>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Zone edit modal
// ---------------------------------------------------------------------------

interface ZoneEditModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly zone: AdminDeliveryZone | null;
  readonly onSave: (payload: UpdateDeliveryZonePayload) => Promise<void>;
  readonly isSaving: boolean;
}

interface ZoneFormState {
  fee_own_car_fcfa: number;
  fee_own_bike_fcfa: string;
  fee_yango_estimated_fcfa: string;
  estimated_time_minutes: number;
  is_serviceable: boolean;
}

function buildFormState(zone: AdminDeliveryZone | null): ZoneFormState {
  if (!zone) {
    return {
      fee_own_car_fcfa: 0,
      fee_own_bike_fcfa: "",
      fee_yango_estimated_fcfa: "",
      estimated_time_minutes: 30,
      is_serviceable: true,
    };
  }
  return {
    fee_own_car_fcfa: zone.fee_own_car_fcfa,
    fee_own_bike_fcfa:
      zone.fee_own_bike_fcfa != null ? String(zone.fee_own_bike_fcfa) : "",
    fee_yango_estimated_fcfa:
      zone.fee_yango_estimated_fcfa != null
        ? String(zone.fee_yango_estimated_fcfa)
        : "",
    estimated_time_minutes: zone.estimated_time_minutes,
    is_serviceable: zone.is_serviceable,
  };
}

function ZoneEditModal({
  open,
  onClose,
  zone,
  onSave,
  isSaving,
}: ZoneEditModalProps) {
  const [form, setForm] = useState<ZoneFormState>(buildFormState(zone));
  const [formError, setFormError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setForm(buildFormState(null));
    setFormError(null);
    onClose();
  }, [onClose]);

  const updateField = useCallback(
    <K extends keyof ZoneFormState>(key: K, value: ZoneFormState[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (form.fee_own_car_fcfa < 0) {
      setFormError("Les frais voiture doivent être positifs.");
      return;
    }
    if (form.estimated_time_minutes < 1) {
      setFormError("Le temps estimé doit être d'au moins 1 minute.");
      return;
    }
    setFormError(null);

    const payload: UpdateDeliveryZonePayload = {
      fee_own_car_fcfa: form.fee_own_car_fcfa,
      fee_own_bike_fcfa:
        form.fee_own_bike_fcfa.trim() !== ""
          ? Number(form.fee_own_bike_fcfa)
          : null,
      fee_yango_estimated_fcfa:
        form.fee_yango_estimated_fcfa.trim() !== ""
          ? Number(form.fee_yango_estimated_fcfa)
          : null,
      estimated_time_minutes: form.estimated_time_minutes,
      is_serviceable: form.is_serviceable,
    };

    try {
      await onSave(payload);
      handleClose();
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : "Une erreur est survenue.",
      );
    }
  }, [form, onSave, handleClose]);

  // Sync form when zone changes
  useEffect(() => {
    if (zone) setForm(buildFormState(zone));
  }, [zone]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title={zone ? `Modifier — ${zone.zone_name}` : "Modifier la zone"}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSaving}
            className="gap-1.5"
          >
            {isSaving ? (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : null}
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Zone name (read-only) */}
        {zone && (
          <div className="rounded-lg border border-stone-100 bg-stone-50 px-3 py-2.5">
            <p className="text-xs text-stone-500">Zone</p>
            <p className="mt-0.5 text-sm font-medium text-stone-900">
              {zone.zone_name}
            </p>
            {zone.region && (
              <p className="mt-0.5 text-xs text-stone-500">
                Région : {zone.region.name}
              </p>
            )}
          </div>
        )}

        {/* Fee — own car */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Frais de livraison — Voiture (FCFA)
          </label>
          <input
            type="number"
            min={0}
            step={100}
            value={form.fee_own_car_fcfa}
            onChange={(e) =>
              updateField("fee_own_car_fcfa", Number(e.target.value))
            }
            className="h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        {/* Fee — own bike */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Frais de livraison — Moto (FCFA)
            <span className="ml-1 text-xs font-normal text-stone-400">
              optionnel
            </span>
          </label>
          <input
            type="number"
            min={0}
            step={100}
            value={form.fee_own_bike_fcfa}
            onChange={(e) => updateField("fee_own_bike_fcfa", e.target.value)}
            placeholder="Non défini"
            className="h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 placeholder:text-stone-400 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        {/* Fee — Yango estimated */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Frais estimés Yango (FCFA)
            <span className="ml-1 text-xs font-normal text-stone-400">
              optionnel
            </span>
          </label>
          <input
            type="number"
            min={0}
            step={100}
            value={form.fee_yango_estimated_fcfa}
            onChange={(e) =>
              updateField("fee_yango_estimated_fcfa", e.target.value)
            }
            placeholder="Non défini"
            className="h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 placeholder:text-stone-400 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        {/* Estimated time */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Temps de livraison estimé (minutes)
          </label>
          <input
            type="number"
            min={1}
            max={1440}
            value={form.estimated_time_minutes}
            onChange={(e) =>
              updateField("estimated_time_minutes", Number(e.target.value))
            }
            className="h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        {/* Serviceable toggle */}
        <div className="flex items-center gap-2">
          <input
            id="zone-serviceable"
            type="checkbox"
            checked={form.is_serviceable}
            onChange={(e) => updateField("is_serviceable", e.target.checked)}
            className="h-4 w-4 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
          />
          <label
            htmlFor="zone-serviceable"
            className="text-sm font-medium text-stone-700"
          >
            Zone desservie (active)
          </label>
        </div>

        {formError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {formError}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function DeliveryZones() {
  const { data, isLoading, isError, refetch } = useDeliveryZones();
  const updateZone = useUpdateDeliveryZone();

  const [search, setSearch] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [serviceableFilter, setServiceableFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [editingZone, setEditingZone] = useState<AdminDeliveryZone | null>(
    null,
  );

  const zones = useMemo(() => data?.zones ?? [], [data?.zones]);

  const filteredZones = useMemo(() => {
    let result = zones;

    // Text search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (z) =>
          z.zone_name.toLowerCase().includes(q) ||
          (z.region?.name ?? "").toLowerCase().includes(q),
      );
    }

    // Region filter
    if (regionFilter) {
      result = result.filter((z) => z.region?.name === regionFilter);
    }

    // Serviceable filter
    if (serviceableFilter === "active") {
      result = result.filter((z) => z.is_serviceable);
    } else if (serviceableFilter === "inactive") {
      result = result.filter((z) => !z.is_serviceable);
    }

    return result;
  }, [zones, search, regionFilter, serviceableFilter]);

  const summary = useMemo(() => computeSummary(zones), [zones]);

  // Group by region for display
  const groupedByRegion = useMemo(() => {
    const map = new Map<string, AdminDeliveryZone[]>();
    for (const zone of filteredZones) {
      const regionName = zone.region?.name ?? "Sans région";
      const existing = map.get(regionName) ?? [];
      map.set(regionName, [...existing, zone]);
    }
    return map;
  }, [filteredZones]);

  const handleEditSave = useCallback(
    async (payload: UpdateDeliveryZonePayload) => {
      if (!editingZone) return;
      await updateZone.mutateAsync({ id: editingZone.id, payload });
      setEditingZone(null);
    },
    [editingZone, updateZone],
  );

  const handleToggleServiceable = useCallback(
    (zone: AdminDeliveryZone) => {
      updateZone.mutate({
        id: zone.id,
        payload: { is_serviceable: !zone.is_serviceable },
      });
    },
    [updateZone],
  );

  const columns: readonly ColumnDef<AdminDeliveryZone>[] = useMemo(
    () => [
      {
        id: "zone_name",
        header: "Zone",
        cell: (z) => (
          <span className="font-medium text-stone-900">{z.zone_name}</span>
        ),
      },
      {
        id: "region",
        header: "Région",
        cell: (z) =>
          z.region ? (
            <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-600">
              {z.region.name}
            </span>
          ) : (
            <span className="text-stone-400">—</span>
          ),
      },
      {
        id: "fee_car",
        header: "Frais voiture",
        cell: (z) => (
          <div className="flex items-center gap-1.5">
            <Truck className="h-3.5 w-3.5 text-stone-400" />
            <span className="text-sm font-medium text-stone-700">
              {formatFcfa(z.fee_own_car_fcfa)}
            </span>
          </div>
        ),
      },
      {
        id: "fee_bike",
        header: "Frais moto",
        cell: (z) => (
          <div className="flex items-center gap-1.5">
            <Bike className="h-3.5 w-3.5 text-stone-400" />
            <span className="text-sm text-stone-600">
              {formatFcfa(z.fee_own_bike_fcfa)}
            </span>
          </div>
        ),
      },
      {
        id: "estimated_time",
        header: "Temps estimé",
        cell: (z) => (
          <div className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-stone-400" />
            <span className="text-sm text-stone-600">
              {formatMinutes(z.estimated_time_minutes)}
            </span>
          </div>
        ),
      },
      {
        id: "is_serviceable",
        header: "Statut",
        cell: (z) => (
          <button
            type="button"
            onClick={() => handleToggleServiceable(z)}
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
              z.is_serviceable
                ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                : "bg-stone-100 text-stone-500 hover:bg-stone-200"
            }`}
          >
            {z.is_serviceable ? "Active" : "Inactive"}
          </button>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: (z) => (
          <button
            type="button"
            onClick={() => setEditingZone(z)}
            className="rounded p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
            aria-label="Modifier"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        ),
      },
    ],
    [handleToggleServiceable],
  );

  return (
    <AdminLayout>
      <ModuleGuard moduleKey="logistics">
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <MapIcon className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Zones de livraison
              </h1>
              <p className="text-sm text-stone-500">
                {isLoading
                  ? "Chargement..."
                  : `${zones.length} zone(s) configurée(s)`}
              </p>
            </div>
          </div>
        </div>

        {/* Summary KPIs */}
        <SummaryCards summary={summary} isLoading={isLoading} />

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-full sm:max-w-xs">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Rechercher par zone ou région..."
            />
          </div>

          <select
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="">Toutes les régions</option>
            {SENEGAL_REGIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>

          <div className="flex rounded-md border border-stone-200 bg-white">
            {(
              [
                { value: "all", label: "Toutes" },
                { value: "active", label: "Actives" },
                { value: "inactive", label: "Inactives" },
              ] as const
            ).map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setServiceableFilter(opt.value)}
                className={`px-3 py-1.5 text-sm transition-colors first:rounded-l-md last:rounded-r-md ${
                  serviceableFilter === opt.value
                    ? "bg-amber-100 text-amber-800"
                    : "text-stone-600 hover:bg-stone-50"
                } ${opt.value !== "all" ? "border-l border-stone-200" : ""}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les zones de livraison.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Réessayer
            </Button>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !isError && filteredZones.length === 0 && (
          <EmptyState
            icon={<MapIcon className="h-6 w-6" />}
            title="Aucune zone trouvée"
            description={
              search || regionFilter || serviceableFilter !== "all"
                ? "Aucune zone ne correspond aux filtres sélectionnés."
                : "Aucune zone de livraison n'est configurée."
            }
          />
        )}

        {/* Zone tables grouped by region */}
        {!isError &&
          Array.from(groupedByRegion.entries()).map(
            ([regionName, regionZones]) => (
              <div key={regionName} className="space-y-2">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold text-stone-700">
                    {regionName}
                  </h2>
                  <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-500">
                    {regionZones.length} zone(s)
                  </span>
                  <span className="flex items-center gap-1 text-xs text-emerald-600">
                    <CheckCircle2 className="h-3 w-3" />
                    {regionZones.filter((z) => z.is_serviceable).length} actives
                  </span>
                  {regionZones.some((z) => !z.is_serviceable) && (
                    <span className="flex items-center gap-1 text-xs text-stone-400">
                      <XCircle className="h-3 w-3" />
                      {regionZones.filter((z) => !z.is_serviceable).length}{" "}
                      inactives
                    </span>
                  )}
                </div>
                <AdminDataTable
                  data={regionZones}
                  columns={columns}
                  isLoading={isLoading}
                />
              </div>
            ),
          )}

        {/* Loading skeleton */}
        {isLoading && (
          <AdminDataTable
            data={[]}
            columns={columns}
            isLoading={true}
          />
        )}
      </div>

      {/* Edit modal */}
      <ZoneEditModal
        open={editingZone !== null}
        onClose={() => setEditingZone(null)}
        zone={editingZone}
        onSave={handleEditSave}
        isSaving={updateZone.isPending}
      />
      </ModuleGuard>
    </AdminLayout>
  );
}
